import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import VerificationHeader from './components/VerificationHeader';
import DealershipInfo from './components/DealershipInfo';
import SecurityNotice from './components/SecurityNotice';
import QuickStats from './components/QuickStats';
import VerificationHistory from './components/VerificationHistory';
import { validateField, voucherValidationSchema } from '../../utils/validation';

const VoucherVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [dealership, setDealership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    loadDealershipData();
  }, [token]);

  const loadDealershipData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch dealership by verification token
      const { data: dealershipData, error: dealershipError } = await supabase
        ?.from('dealerships')
        ?.select('*')
        ?.eq('verification_token', token)
        ?.single();

      if (dealershipError) {
        if (dealershipError?.code === 'PGRST116') {
          setError('Ungültiger oder abgelaufener Verifizierungs-Link');
        } else {
          throw dealershipError;
        }
        return;
      }

      // Check token expiration
      if (dealershipData?.token_expires_at) {
        const expiresAt = new Date(dealershipData.token_expires_at);
        if (expiresAt < new Date()) {
          setError('Dieser Verifizierungs-Link ist abgelaufen. Bitte kontaktieren Sie den Administrator.');
          return;
        }
      }

      setDealership(dealershipData);
    } catch (err) {
      console.error('Error loading dealership:', err);
      setError('Fehler beim Laden der Partner-Daten');
    } finally {
      setLoading(false);
    }
  };

  const formatVoucherCode = (value) => {
    const cleaned = value?.replace(/[^A-Z0-9]/g, '')?.toUpperCase();
    const parts = [];
    for (let i = 0; i < cleaned?.length; i += 4) {
      parts?.push(cleaned?.substring(i, i + 4));
    }
    return parts?.join('-')?.substring(0, 14);
  };

  const handleCodeChange = (e) => {
    const formatted = formatVoucherCode(e?.target?.value);
    setVoucherCode(formatted);
    setError('');
    setFieldError('');
    setVerificationResult(null);
    
    // Real-time validation
    if (formatted?.length === 14) {
      const validationError = validateField('voucherCode', formatted, voucherValidationSchema?.voucherCode);
      if (validationError) {
        setFieldError(validationError);
      }
    }
  };

  const handleVerification = async (e) => {
    e?.preventDefault();
    
    // Validate format
    const validationError = validateField('voucherCode', voucherCode, voucherValidationSchema?.voucherCode);
    if (validationError) {
      setFieldError(validationError);
      return;
    }

    setVerifying(true);
    setError('');
    setFieldError('');
    setVerificationResult(null);

    try {
      // Verify voucher code exists and get details
      const { data: voucher, error: voucherError } = await supabase
        ?.from('vouchers')
        ?.select('*, applications(*)')
        ?.eq('voucher_code', voucherCode?.trim()?.toUpperCase())
        ?.single();

      if (voucherError || !voucher) {
        setVerificationResult({
          success: false,
          message: 'Ungültiger Gutscheincode - Code nicht in der Datenbank gefunden'
        });
        return;
      }

      // SECURITY CHECK: Verify voucher belongs to this dealership
      if (voucher?.dealership_id !== dealership?.id) {
        setVerificationResult({
          success: false,
          message: 'Sicherheitsfehler: Dieser Gutschein wurde über einen anderen Partner registriert und kann nur von diesem eingelöst werden.',
          details: null // Don't expose voucher details for security
        });
        return;
      }

      // Check if application is already redeemed
      if (voucher?.applications?.application_status === 'redeemed') {
        const redemptionDate = voucher?.verification_date 
          ? new Date(voucher?.verification_date)?.toLocaleDateString('de-DE', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'Datum unbekannt';
        setVerificationResult({
          success: false,
          message: `Dieser Gutschein wurde bereits eingelöst am ${redemptionDate}`,
          details: voucher
        });
        return;
      }

      // Check voucher status
      if (voucher?.voucher_status === 'used') {
        const redemptionDate = voucher?.verification_date 
          ? new Date(voucher?.verification_date)?.toLocaleDateString('de-DE', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'Datum unbekannt';
        setVerificationResult({
          success: false,
          message: `Dieser Gutschein wurde bereits eingelöst am ${redemptionDate}`,
          details: voucher
        });
        return;
      }

      if (voucher?.voucher_status === 'expired') {
        setVerificationResult({
          success: false,
          message: 'Dieser Gutschein ist abgelaufen',
          details: voucher
        });
        return;
      }

      if (voucher?.voucher_status === 'cancelled') {
        setVerificationResult({
          success: false,
          message: 'Dieser Gutschein wurde storniert',
          details: voucher
        });
        return;
      }

      // Create verification record
      const { error: verificationError } = await supabase
        ?.from('voucher_verifications')
        ?.insert([
          {
            voucher_id: voucher?.id,
            dealership_id: dealership?.id,
            verification_date: new Date()?.toISOString(),
            verification_method: 'web_portal',
            verified_by: dealership?.contact_person || dealership?.name
          }
        ]);

      if (verificationError) throw verificationError;

      // Update voucher status to 'used' and verification count
      const newCount = (voucher?.verification_count || 0) + 1;
      await supabase
        ?.from('vouchers')
        ?.update({ 
          voucher_status: 'used',
          verification_count: newCount,
          verification_date: new Date()?.toISOString()
        })
        ?.eq('id', voucher?.id);

      // Update application status to 'redeemed'
      if (voucher?.application_id) {
        const { error: applicationUpdateError } = await supabase
          ?.from('applications')
          ?.update({ 
            application_status: 'redeemed',
            updated_at: new Date()?.toISOString()
          })
          ?.eq('id', voucher?.application_id);

        if (applicationUpdateError) {
          console.error('Failed to update application status:', applicationUpdateError);
        }
      }

      setVerificationResult({
        success: true,
        message: 'Gutschein erfolgreich verifiziert',
        details: voucher
      });

      // Clear input
      setVoucherCode('');

      // Reload data to update stats
      await loadDealershipData();

    } catch (err) {
      console.error('Verification error:', err);
      setError('Fehler bei der Verifizierung. Bitte versuchen Sie es erneut.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Lade Verifizierungsseite...</p>
        </div>
      </div>
    );
  }

  if (error && !dealership) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-lg shadow-card p-8 text-center">
          <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold text-foreground mb-2">
            Zugriff nicht möglich
          </h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button
            variant="default"
            fullWidth
            iconName="Home"
            iconPosition="left"
            onClick={() => navigate('/')}
          >
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <VerificationHeader dealership={dealership} dealershipName={dealership?.name} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <DealershipInfo dealership={dealership} />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Verification Form */}
            <div className="bg-card rounded-lg shadow-card p-6">
              <h2 className="text-xl font-heading font-bold text-foreground mb-4">
                Gutschein verifizieren
              </h2>
              
              <form onSubmit={handleVerification} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Gutscheincode eingeben
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={voucherCode}
                      onChange={handleCodeChange}
                      placeholder="THG-XXXX-XXXX"
                      className={`flex-1 px-4 py-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-lg ${
                        fieldError ? 'border-error' : 'border-border'
                      }`}
                      disabled={verifying}
                      maxLength={14}
                      aria-invalid={!!fieldError}
                    />
                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      loading={verifying}
                      disabled={voucherCode?.length !== 14 || verifying || !!fieldError}
                      iconName="CheckCircle2"
                      iconPosition="left"
                    >
                      Prüfen
                    </Button>
                  </div>
                  {fieldError && (
                    <p className="text-sm text-error mt-2 flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} />
                      {fieldError}
                    </p>
                  )}
                </div>

                {verificationResult && (
                  <div className={`p-4 rounded-lg ${
                    verificationResult?.success 
                      ? 'bg-success/10 border border-success/20' : 'bg-error/10 border border-error/20'
                  }`}>
                    <div className="flex items-start gap-3">
                      <Icon 
                        name={verificationResult?.success ? 'CheckCircle2' : 'XCircle'} 
                        size={24} 
                        color={verificationResult?.success ? 'var(--color-success)' : 'var(--color-error)'} 
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${
                          verificationResult?.success ? 'text-success' : 'text-error'
                        }`}>
                          {verificationResult?.message}
                        </p>
                        {verificationResult?.details && (
                          <div className="mt-2 text-sm text-muted-foreground space-y-1">
                            <p>Kunde: {verificationResult?.details?.applications?.customer_name}</p>
                            <p>Fahrzeug: {verificationResult?.details?.applications?.vehicle_model || 'Nicht angegeben'}</p>
                            <p>Status: {verificationResult?.details?.voucher_status}</p>
                            {verificationResult?.success && (
                              <p className="text-success font-medium mt-2">
                                Verifizierung erfolgreich durchgeführt
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            <VerificationHistory dealershipId={dealership?.id} verifications={[]} />
          </div>

          <div className="space-y-8">
            <QuickStats dealershipId={dealership?.id} stats={{}} />
            <SecurityNotice />
          </div>
        </div>
      </main>
    </div>
  );
};

export default VoucherVerification;